/**
 * The display fields each lens keys on.
 *
 * Naming a field is itself a disclosure — "this org tracks flight risk" is
 * sensitive even when the values are redacted away. So a lens the viewer is not
 * granted also blocks its fields from any *persisted* rule, which is how a
 * chart default set by someone with broader access stays invisible to someone
 * without it. Lenses that colour by structure alone (vacancy, succession
 * badges) name nothing sensitive and are absent by design.
 *
 * Shared by the editor (`useOrgAnalyticsLens`) and the server-side export, so
 * an exported lens is blocked by exactly the same rule as the one on screen.
 */
export const LENS_KEY_TO_DISPLAY_FIELDS: Record<string, readonly string[]> = {
  'span-of-control': ['directReports'],
  'performance-distribution': ['performance'],
  'compensation-bands': ['salary', 'compaRatio'],
  'tenure-distribution': ['tenure'],
  'flight-risk': ['flightRisk'],
};

export const ALL_GOVERNED_LENS_FIELDS: readonly string[] = Object.values(
  LENS_KEY_TO_DISPLAY_FIELDS,
).flat();

/**
 * Display fields the viewer has no lens grant for. An unknown eligibility
 * (not yet answered, or absent from the model) blocks every governed field:
 * over-blocking costs a missing legend, under-blocking discloses.
 */
export const resolveBlockedLensDisplayFields = (
  eligible: readonly { readonly key: string }[] | undefined,
): string[] => {
  if (!eligible) return [...ALL_GOVERNED_LENS_FIELDS];
  const grantedKeys = new Set(eligible.map((preset) => preset.key));
  return Object.entries(LENS_KEY_TO_DISPLAY_FIELDS)
    .filter(([key]) => !grantedKeys.has(key))
    .flatMap(([, fields]) => fields);
};
